import { type StoreApi, type UseBoundStore, useStore } from 'zustand'
import { useShallow } from 'zustand/react/shallow'

// Maps each store key to a no-arg hook that returns that key's value.
// e.g. { count: number; increment: () => void } → { count: () => number; increment: () => () => void }
type StoreSelectors<T> = {
  [K in keyof T]: () => T[K]
}

/**
 * Wraps a Zustand store to auto-generate `.use.key()` hook selectors for every
 * key in the store, plus optional derived selectors that compute values from state.
 *
 * **Store key selectors** subscribe to a single store key via `useStore(store, selector)`.
 * Actions created with Immer middleware are referentially stable, so selecting an action
 * never causes unnecessary re-renders.
 *
 * **Derived selectors** compute values from store state and are wrapped with `useShallow`
 * so components only re-render when the shallow-compared result actually changes.
 *
 * WARNING: `useShallow` only compares one level deep. Derived selectors that construct
 * new nested objects (e.g. `{ and: [{ or: [...] }] }`) will always fail the shallow
 * check, causing infinite re-renders. For such cases, use a standalone hook with
 * `useMemo` or keep the computation in React context instead.
 *
 * Safe to return: primitives, flat arrays, flat objects with primitive values, or
 * direct references from the store (not newly constructed).
 *
 * Store shape must be fully defined at creation time — selectors are generated from
 * `Object.keys(store.getState())` once and will not pick up keys added later via `setState`.
 *
 * @param store - A Zustand store created with `create()`. Supports middleware (devtools, immer, etc.).
 * @param derivedSelectors - Optional map of selector functions that derive values from
 *   store state. Each function receives the full store state and returns a computed value.
 * @returns The original store augmented with a `.use` namespace containing hook selectors.
 *
 * @example
 * // Basic usage — store key selectors only
 * const useCounterStoreBase = create<{ count: number; increment: () => void }>()((set) => ({
 *   count: 0,
 *   increment: () => set((s) => ({ count: s.count + 1 })),
 * }))
 *
 * const useCounterStore = createSelectors(useCounterStoreBase)
 *
 * function Counter() {
 *   const count = useCounterStore.use.count()
 *   const increment = useCounterStore.use.increment()
 *   return <button onClick={increment}>{count}</button>
 * }
 *
 * @example
 * // With derived selectors (including middleware)
 * interface MyStore { items: string[]; filter: string }
 *
 * const useStoreBase = create<MyStore>()(
 *   devtools(immer((set) => ({ items: ['apple', 'banana'], filter: '' })))
 * )
 *
 * const useStore = createSelectors(useStoreBase, {
 *   filteredItems: (state) =>
 *     state.items.filter((item) => item.includes(state.filter)),
 * })
 *
 * function List() {
 *   const filteredItems = useStore.use.filteredItems() // shallow-compared, re-renders only when result changes
 * }
 */
export function createSelectors<
  T,
  R extends Record<string, unknown> = Record<never, unknown>,
>(
  store: UseBoundStore<StoreApi<T>>,
  derivedSelectors?: { [K in keyof R]: (state: T) => R[K] },
): UseBoundStore<StoreApi<T>> & {
  use: Omit<StoreSelectors<T>, keyof R> & { [K in keyof R]: () => R[K] }
} {
  type UseMap = Omit<StoreSelectors<T>, keyof R> & {
    [K in keyof R]: () => R[K]
  }
  const augmented = store as UseBoundStore<StoreApi<T>> & { use: UseMap }
  const useMap: Record<string, () => unknown> = {}

  for (const key of Object.keys(store.getState() as Record<string, unknown>)) {
    const selector = (state: T) => (state as Record<string, unknown>)[key]
    useMap[key] = () => useStore(store, selector)
  }

  if (derivedSelectors) {
    for (const [key, selectorFn] of Object.entries(derivedSelectors)) {
      useMap[key] = () => useStore(store, useShallow(selectorFn))
    }
  }

  augmented.use = useMap as UseMap
  return augmented
}
